import {
  CONSUMER_CMS_SHOPPING_CART,
  IGenericErrorCMS,
  IGetAddonResponse,
  IOfferingType,
  useAddAddon,
  useCmsConfig,
  useGetShoppingCartSilentUpdate,
  useRemoveAddon,
} from '@vfit/consumer/data-access';
import { API, getFromLocalStorageByKey, LoggerInstance } from '@vfit/shared/data-access';
import { useEffect } from 'react';
import { IUpdateProductPriceType } from '@vfit/shared/models';
import { updateProductPrice } from '@vfit/consumer/ibuyfixed';
import { useQueryClient } from 'react-query';
import { useCheckout } from '../../../iBuyFixed.context';
import { DEFAULT_ADDON_STATE } from '../../../iBuyFixed.context.data';
import {
  getReferenceId,
  getReferenceIdFromQueryClient,
  removeAddonFromQueryClient,
  retrieveAddonTypeById,
  saveAddonOnQueryClient,
} from './useAddonFlow.utils';

export const useAddonFlow = () => {
  const { addons, setAddons, addonState, setAddonState, product, setProduct, checkoutErrors } =
    useCheckout();

  const { addonCmsId, addonMecId, availableAddons, callSaveAddon, callRemoveAddon } = addonState;

  const shoppingCart = getFromLocalStorageByKey('shoppingCart');
  const cartId: string = shoppingCart?.id || '';

  const queryClient = useQueryClient();
  const getAddonsDataFromQueryClient = queryClient.getQueryData(
    'getAvailableAddons'
  ) as IGetAddonResponse;

  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;

  const {
    mutate: saveAddonMutation,
    data: saveAddonData,
    isSuccess: isSuccessAddition,
    isError: isErrorAddition,
    isLoading: isLoadingAddition,
  } = useAddAddon();

  const {
    mutate: removeAddonMutation,
    data: removeAddonData,
    isSuccess: isSuccessRemoval,
    isError: isErrorRemoval,
    isLoading: isLoadingRemoval,
  } = useRemoveAddon();

  const {
    isSuccess: isSuccessShoppingCart,
    data: shoppingCartData,
    refetch: refetchShoppingCart,
    isFetching: isLoadingShoppingCart,
    isError: isErrorShoppingCart,
  } = useGetShoppingCartSilentUpdate(cartId, {
    enabled: false,
    keyDependency: ['getShoppingCartAddons'],
  });

  const saveAddon = () => {
    if (addonCmsId && addonMecId) {
      const offeringType =
        retrieveAddonTypeById(addonMecId, getAddonsDataFromQueryClient) ||
        IOfferingType.FIXED_VOICE;
      saveAddonMutation({ cartId, addonId: addonMecId, type: offeringType });
    } else {
      LoggerInstance.error('Error in offer update');
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  };

  const removeAddon = () => {
    if (addonCmsId && addonMecId) {
      const offeringType =
        retrieveAddonTypeById(addonMecId, getAddonsDataFromQueryClient) ||
        IOfferingType.FIXED_VOICE;
      const referenceId = getReferenceIdFromQueryClient(queryClient, addonMecId);
      removeAddonMutation({ cartId, addonId: referenceId, type: offeringType });
    } else {
      LoggerInstance.error('Error in offer update');
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  };

  useEffect(() => {
    if (callSaveAddon) {
      saveAddon();
    }
    if (callRemoveAddon) {
      removeAddon();
    }
  }, [callSaveAddon, callRemoveAddon]);

  useEffect(() => {
    if (isSuccessAddition && saveAddonData) {
      const offeringType =
        retrieveAddonTypeById(addonMecId, getAddonsDataFromQueryClient) ||
        IOfferingType.FIXED_VOICE;
      const referenceId = getReferenceId(offeringType, addonMecId, saveAddonData);
      saveAddonOnQueryClient(queryClient, addonMecId, referenceId, offeringType);
      setAddons([...addons, addonCmsId]);
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      refetchShoppingCart();
    }
  }, [isSuccessAddition, saveAddonData]);

  useEffect(() => {
    if (isSuccessRemoval && removeAddonData) {
      setAddons(addons.filter((addn) => addn !== addonCmsId));
      removeAddonFromQueryClient(queryClient, addonMecId);
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      refetchShoppingCart();
    }
  }, [isSuccessRemoval, removeAddonData]);

  useEffect(() => {
    const addonJson = JSON.stringify(addons);
    localStorage.setItem('addons', addonJson);
  }, [addons]);

  useEffect(() => {
    if (isErrorAddition || isErrorRemoval || isErrorShoppingCart) {
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
      checkoutErrors?.showAndTrackError?.(genericError);
    }
  }, [isErrorAddition, isErrorRemoval, isErrorShoppingCart]);

  useEffect(() => {
    if (isSuccessShoppingCart && shoppingCartData) {
      setProduct({
        ...product,
        ...updateProductPrice(product, IUpdateProductPriceType.EDIT),
      });
    }
    setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
  }, [isSuccessShoppingCart, shoppingCartData]);

  useEffect(() => {
    if (isLoadingAddition || isLoadingRemoval) {
      setAddonState({
        ...addonState,
        isLoadingUpdate: true,
      });
    }
  }, [isLoadingAddition, isLoadingRemoval]);

  useEffect(() => {
    if (isLoadingShoppingCart) {
      setAddonState({
        ...addonState,
        isLoadingPriceUpdate: true,
      });
    } else {
      setAddonState({ ...DEFAULT_ADDON_STATE, availableAddons });
    }
  }, [isLoadingShoppingCart]);

  return null;
};

